import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { API_URL } from "../../enums/api.enums";
import HttpClient from "../http-client";
import Utils from "../../utils/base-utils";
import {
    ICreateVipTierRequest,
    IUpdateVipTierRequest,
    IGetVipUsersRequest,
    IVipTier,
    IUserVip,
    IUserVipNoVip,
    IVipUpgradeResponse,
    IVipDiscountCalculation,
    IVipUsersList
} from "../../common/vip.interface";

export default class VipApi {
    static apiURL = API_URL;

    // =============== VIP TIERS MANAGEMENT ===============

    // GET /vip/tiers - Lấy danh sách tất cả VIP tiers
    static getVipTiers(): Observable<IVipTier[]> {
        const api = `${VipApi.apiURL.HOST}/vip/tiers`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || []),
            catchError((error) => {
                console.error('Error fetching VIP tiers:', error);
                return of([]);
            })
        );
    }

    // GET /vip/tiers/:id - Lấy chi tiết VIP tier theo ID
    static getVipTierById(id: number): Observable<IVipTier> {
        const api = `${VipApi.apiURL.HOST}/vip/tiers/${id}`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error fetching VIP tier:', error);
                return of(null as any);
            })
        );
    }

    // POST /vip/tiers - Tạo VIP tier mới (Admin only)
    static createVipTier(body: ICreateVipTierRequest): Observable<IVipTier> {
        const api = `${VipApi.apiURL.HOST}/vip/tiers`;
        return HttpClient.post(api, body).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error creating VIP tier:', error);
                return of(null as any);
            })
        );
    }

    // PUT /vip/tiers/:id - Cập nhật VIP tier (Admin only)
    static updateVipTier(id: number, body: IUpdateVipTierRequest): Observable<IVipTier> {
        const api = `${VipApi.apiURL.HOST}/vip/tiers/${id}`;
        return HttpClient.put(api, body).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error updating VIP tier:', error);
                return of(null as any);
            })
        );
    }

    // DELETE /vip/tiers/:id - Xóa VIP tier (Admin only)
    static deleteVipTier(id: number): Observable<{ message: string }> {
        const api = `${VipApi.apiURL.HOST}/vip/tiers/${id}`;
        return HttpClient.delete(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error deleting VIP tier:', error);
                return of({ message: 'Error deleting VIP tier' });
            })
        );
    }

    // =============== USER VIP MANAGEMENT ===============

    // GET /vip/users/me - Lấy VIP của user hiện tại
    static getCurrentUserVip(): Observable<IUserVip | IUserVipNoVip> {
        const api = `${VipApi.apiURL.HOST}/vip/users/me`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error fetching current user VIP:', error);
                return of(null as any);
            })
        );
    }

    // GET /vip/users/:userId - Lấy VIP của user theo ID (Admin only)
    static getUserVipById(userId: number): Observable<IUserVip | IUserVipNoVip> {
        const api = `${VipApi.apiURL.HOST}/vip/users/${userId}`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error fetching user VIP:', error);
                return of(null as any);
            })
        );
    }

    // POST /vip/users/me/check-upgrade - Kiểm tra và nâng cấp VIP cho user hiện tại
    static checkUpgradeCurrentUser(): Observable<IVipUpgradeResponse> {
        const api = `${VipApi.apiURL.HOST}/vip/users/me/check-upgrade`;
        return HttpClient.post(api, {}).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error checking VIP upgrade for current user:', error);
                return of(null as any);
            })
        );
    }

    // POST /vip/users/:userId/check-upgrade - Kiểm tra và nâng cấp VIP cho user theo ID (Admin only)
    static checkUpgradeUserById(userId: number): Observable<IVipUpgradeResponse> {
        const api = `${VipApi.apiURL.HOST}/vip/users/${userId}/check-upgrade`;
        return HttpClient.post(api, {}).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error checking VIP upgrade for user:', error);
                return of(null as any);
            })
        );
    }

    // GET /vip/users/me/discount-calculation?amount=100000 - Tính toán giảm giá VIP cho user hiện tại
    static calculateDiscountCurrentUser(amount: number): Observable<IVipDiscountCalculation> {
        const api = `${VipApi.apiURL.HOST}/vip/users/me/discount-calculation?amount=${amount}`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error calculating discount for current user:', error);
                return of(null as any);
            })
        );
    }

    // GET /vip/users - Lấy danh sách users có VIP (Admin only)
    static getVipUsers(params: IGetVipUsersRequest = {}): Observable<IVipUsersList> {
        const queryParam = Utils.parseObjectToQueryParameter(params);
        const api = `${VipApi.apiURL.HOST}/vip/users${queryParam}`;
        return HttpClient.get(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error fetching VIP users:', error);
                return of(null as any);
            })
        );
    }

    // DELETE /vip/users/:userId - Xóa VIP của user (Admin only)
    static deleteUserVip(userId: number): Observable<{ message: string }> {
        const api = `${VipApi.apiURL.HOST}/vip/users/${userId}`;
        return HttpClient.delete(api).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Error deleting user VIP:', error);
                return of({ message: 'Error deleting user VIP' });
            })
        );
    }

    // =============== UTILITY METHODS ===============

    // Batch check upgrade for multiple users (Admin utility)
    static batchCheckUpgrade(userIds: number[]): Observable<IVipUpgradeResponse[]> {
        return new Observable(observer => {
            const results: IVipUpgradeResponse[] = [];
            let completed = 0;

            userIds.forEach(userId => {
                this.checkUpgradeUserById(userId).subscribe({
                    next: (result) => {
                        results.push(result);
                        completed++;
                        if (completed === userIds.length) {
                            observer.next(results);
                            observer.complete();
                        }
                    },
                    error: (error) => {
                        console.error(`Error checking upgrade for user ${userId}:`, error);
                        completed++;
                        if (completed === userIds.length) {
                            observer.next(results);
                            observer.complete();
                        }
                    }
                });
            });
        });
    }
}
